import { useEffect, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { ExportMenu } from "./ExportMenu";
import { useWidgetData } from "../hooks/useWidgetData";
import {
  buildOption,
  buildStat,
  formatCompact,
  formatNumber,
  prettyLabel,
} from "../lib/chart";
import type { DataFrame, Widget } from "../types";

export function WidgetCard({ widget }: { widget: Widget }) {
  const data = useWidgetData(widget.source);

  return (
    <div className="widget-card">
      <div className="widget-head">
        <span className="widget-title">{widget.title}</span>
        {/* Export sits on the visual, like Power BI -- and only where it has a
            meaning: a chart's underlying rows are the table's, not its own. */}
        {widget.type === "table" && (
          <ExportMenu sql={widget.source.query} title={widget.title} />
        )}
      </div>
      <div className="widget-body">
        {data.status === "loading" && <div className="widget-empty">Loading...</div>}
        {data.status === "error" && (
          <div className="widget-empty widget-err">{data.error}</div>
        )}
        {data.status === "ok" && renderBody(widget, data)}
      </div>
    </div>
  );
}

/** Row height in px. Must match `.table-wrap td` line-height + padding, because
 *  the virtualizer positions rows arithmetically rather than measuring them. */
const ROW_H = 30;

/** Extra rows rendered above and below the viewport so a fast scroll doesn't
 *  show blank space before the next frame. */
const OVERSCAN = 12;

/**
 * Says how many rows the table holds, and whether the server had more.
 *
 * With the body virtualized, "shown" and "held" are now the same number — the
 * only remaining way to see less than the truth is the server's row cap, and it
 * says so. On a financial report a silently-shortened table is not a display
 * glitch; it is a wrong number that looks right.
 */
function TableFooter({ total, truncated }: { total: number; truncated?: boolean }) {
  const parts = [`${fmt(total)} rows`];
  if (truncated) parts.push("server row cap reached — narrow the filters or export");

  return (
    <div className={`table-footer${truncated ? " warn" : ""}`}>{parts.join(" · ")}</div>
  );
}

/**
 * A windowed table body.
 *
 * Every row used to be a real `<tr>`, which is why the component quietly
 * rendered only the first 2,000 — 80,000 `<tr>` elements freeze the tab. Here
 * only the rows in view are in the DOM; the rest are two spacer rows whose
 * height stands in for them, so the scrollbar is the full length and the row
 * count on screen is the row count in hand.
 */
function VirtualTableBody({
  df,
  decimals,
}: {
  df: DataFrame;
  decimals: Set<string>;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [range, setRange] = useState({ start: 0, end: 60 });

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const recompute = () => {
      const first = Math.floor(el.scrollTop / ROW_H);
      const visible = Math.ceil(el.clientHeight / ROW_H);
      setRange({
        start: Math.max(0, first - OVERSCAN),
        end: Math.min(df.rows.length, first + visible + OVERSCAN),
      });
    };
    recompute();
    el.addEventListener("scroll", recompute, { passive: true });
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", recompute);
      ro.disconnect();
    };
  }, [df.rows.length]);

  const padTop = range.start * ROW_H;
  const padBottom = Math.max(0, (df.rows.length - range.end) * ROW_H);

  return (
    <div className="table-scroll" ref={scrollRef}>
      <table>
        <thead>
          <tr>
            {df.columns.map((c) => (
              <th key={c.name} className={c.type === "number" ? "num" : undefined}>
                {prettyLabel(c.name)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {padTop > 0 && <tr style={{ height: padTop }} aria-hidden />}
          {df.rows.slice(range.start, range.end).map((r, i) => (
            <tr key={range.start + i}>
              {df.columns.map((c) => (
                <td key={c.name} className={c.type === "number" ? "num" : undefined}>
                  {formatCell(
                    r[c.name],
                    c.type === "number",
                    decimals.has(c.name),
                    isIdentifier(c.name),
                  )}
                </td>
              ))}
            </tr>
          ))}
          {padBottom > 0 && <tr style={{ height: padBottom }} aria-hidden />}
        </tbody>
      </table>
    </div>
  );
}

const fmt = (n: number) => n.toLocaleString("en-US");

/**
 * Which numeric columns hold fractional values.
 *
 * Decimals are a property of the COLUMN, not of each value. A money column with
 * one whole amount in it must still render `1 000.00`, or that row's decimal
 * point sits a character left of every other row and the column stops lining
 * up. Conversely a year or a row count must never become `2 026.00`.
 *
 * So: scan once, decide per column, format every cell in it the same way.
 */
function decimalColumns(df: DataFrame): Set<string> {
  const out = new Set<string>();
  for (const c of df.columns) {
    if (c.type !== "number") continue;
    for (const row of df.rows) {
      const n = Number(row[c.name]);
      if (Number.isFinite(n) && !Number.isInteger(n)) {
        out.add(c.name);
        break;
      }
    }
  }
  return out;
}

/**
 * Numbers that are labels, not quantities: years and identifiers.
 *
 * Thousands separators are for magnitudes. A year rendered `2 026` and a payout
 * id rendered `1 234 567` both read as money that isn't. Grouping is decided by
 * the column's NAME because nothing in the value distinguishes the year 2026
 * from the quantity 2026.
 */
const IDENTIFIER_COLUMN = /(^|[\s_])(year|id|code|no)$/i;

const isIdentifier = (name: string) => IDENTIFIER_COLUMN.test(name.trim());

/** Render one cell: separated thousands for quantities, raw text otherwise. */
function formatCell(
  value: unknown,
  isNumber: boolean,
  decimals: boolean,
  plain: boolean,
): string {
  if (value === null || value === undefined) return "";
  if (!isNumber) return String(value);
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (plain) return String(n);
  return decimals ? formatNumber(Number(n.toFixed(2))) + padDecimals(n) : formatNumber(n);
}

/** `formatNumber` drops a trailing `.00`; a decimal column needs it back. */
function padDecimals(n: number): string {
  return Number.isInteger(Number(n.toFixed(2))) ? ".00" : "";
}

function renderBody(widget: Widget, df: DataFrame) {
  if (widget.type === "stat") {
    return (
      <div className="stat">
        <div className="stat-value" title={formatNumber(buildStat(widget, df))}>
          {formatCompact(buildStat(widget, df))}
        </div>
      </div>
    );
  }

  if (widget.type === "table") {
    return (
      <div className="table-wrap">
        <VirtualTableBody df={df} decimals={decimalColumns(df)} />
        <TableFooter total={df.rows.length} truncated={df.truncated} />
      </div>
    );
  }

  // line / bar / pie
  return (
    <ReactECharts
      key={widget.type}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      option={buildOption(widget, df) as any}
      notMerge
      style={{ height: "100%", width: "100%" }}
    />
  );
}
